import { inject } from '@angular/core';
import { CanActivateFn, Router } from '@angular/router';
import { SettingsAccessService } from '../components/client-super-admin/settings-access.service';

const ORG_MUTATION_URL_KEYS = [
  'edit-details',
  'edit-organization-details',
  'add-office-location',
  'edit-office-location',
] as const;

function isOrgMutationUrl(url: string): boolean {
  return ORG_MUTATION_URL_KEYS.some((key) => url.includes(key));
}

/**
 * Parent Settings route: allow when module 12 is accessible
 * (or org/system admin). Redirects to home when Settings is denied.
 */
export const settingsModuleGuard: CanActivateFn = () => {
  const access = inject(SettingsAccessService);
  const router = inject(Router);
  access.refresh();
  if (access.canAccessSettings()) {
    return true;
  }
  return router.createUrlTree(['/dashboard', 'home']);
};

/**
 * Settings child routes: allow only the tabs granted under module 12.
 * Unrecognized child paths (edit sub-routes) fall through when Settings is accessible.
 */
export const settingsTabGuard: CanActivateFn = (route, state) => {
  const access = inject(SettingsAccessService);
  const router = inject(Router);
  access.refresh();

  if (!access.canAccessSettings()) {
    return router.createUrlTree(['/dashboard', 'home']);
  }

  if (isOrgMutationUrl(state.url) && !access.canOrgUpdate()) {
    return router.createUrlTree(['/dashboard', 'settings', 'organization-details']);
  }

  const tab = access.resolveTabByUrl(state.url);
  if (!tab) {
    return true;
  }
  if (access.isRouteAccessible(tab.route)) {
    return true;
  }

  const fallback = access.firstAccessibleRoute();
  if (fallback) {
    return router.createUrlTree(['/dashboard', 'settings', fallback]);
  }
  return router.createUrlTree(['/dashboard', 'home']);
};

/** Employee add/edit forms live outside the Settings child tree. */
export const settingsEmployeeUpdateGuard: CanActivateFn = () => {
  const access = inject(SettingsAccessService);
  const router = inject(Router);
  access.refresh();
  if (access.canEmployeeUpdate()) {
    return true;
  }
  return router.createUrlTree(['/dashboard', 'settings', 'user-management']);
};

/** Organization edit forms that sit outside `/settings`. */
export const settingsOrgUpdateGuard: CanActivateFn = () => {
  const access = inject(SettingsAccessService);
  const router = inject(Router);
  access.refresh();
  if (access.canOrgUpdate()) {
    return true;
  }
  return router.createUrlTree(['/dashboard', 'settings', 'organization-details']);
};

/** Role editor lives outside the Settings child tree. */
export const settingsRolesWriteGuard: CanActivateFn = () => {
  const access = inject(SettingsAccessService);
  const router = inject(Router);
  access.refresh();
  const isEdit = !!localStorage.getItem('RoleId');
  if (isEdit ? access.canRolesUpdate() : access.canRolesCreate()) {
    return true;
  }
  return router.createUrlTree(['/dashboard', 'settings', 'roles-&-permissions']);
};
